import { IPollMyUnit } from '../../frontend-libs/evlapp-types';

import { Text, TipMessage } from '../../common/components';
import { t } from '../../common/i18n';

interface MyVotingUnitsProps {
  units: IPollMyUnit[];
}

const formatPercent = (numerator: number, denominator: number): string =>
  ((numerator / denominator) * 100).toLocaleString('cs-CZ', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/**
 * „Za co hlasuji" — přehled jednotek člena v hlasování vč. vysvětlení odděleného
 * (kvórového) hlasování a zastoupení. Jednotky vyloučené z hlasování se nezobrazují;
 * vlastní jednotka se společným zástupcem (jiným než já) se zobrazuje jako informace.
 */
export const MyVotingUnits: React.FC<MyVotingUnitsProps> = ({ units }) => {
  const namespace = 'Poll.Detail.MyVotingUnits';

  const votable = units.filter((unit) => unit.mode !== 'excluded');
  if (votable.length === 0) return null;

  const quorumUnits = votable.filter((unit) => unit.mode === 'quorum');
  const onlyQuorum = quorumUnits.length === votable.length;

  // „Už bylo hlasováno (kým)" — ověřený hlas jednotky; u kvórové jednotky ověřený vnitřní hlas přihlášeného
  const votedStatus = (unit: IPollMyUnit): string | null => {
    if (!unit.voted) return null;
    if (unit.votedByMe) return t(unit.mode === 'quorum' ? 'votedByMeQuorum' : 'votedByMe', namespace);
    if (unit.votedAs === 'admin') return t('votedByAdmin', namespace);
    if (unit.votedByName) {
      return t(unit.votedAs === 'representative' ? 'votedByRepresentative' : 'votedByOther', namespace, {
        name: unit.votedByName,
      });
    }
    return t('votedUnknown', namespace);
  };

  return (
    <div className="mb-3">
      <Text as="div" size="body-medium" weight="600" color="Neutral800" className="mb-1">
        {t('title', namespace)}
      </Text>
      {votable.map((unit) => (
        <Text as="div" size="body-small" color="Neutral700" key={unit.unitId} className="mb-1">
          {t('unit', namespace, { identification: unit.identification })}
          {' — '}
          {t('share', namespace, {
            share: `${unit.shareNumerator}/${unit.shareDenominator}`,
            percent: formatPercent(unit.shareNumerator, unit.shareDenominator),
          })}
          {unit.mode === 'quorum' && (
            <>
              {' · '}
              {t('quorumLabel', namespace)}
              {unit.ownerShareNumerator !== null && unit.ownerShareDenominator !== null && (
                <>
                  {' ('}
                  {t('ownerShare', namespace, { share: `${unit.ownerShareNumerator}/${unit.ownerShareDenominator}` })}
                  {')'}
                </>
              )}
            </>
          )}
          {unit.mode === 'designated' && unit.role === 'owner' && (
            <>
              {' · '}
              {unit.representativeName
                ? t('designatedByOther', namespace, { name: unit.representativeName })
                : t('designatedByMe', namespace)}
            </>
          )}
          {unit.role === 'representative' && (
            <>
              {' · '}
              {unit.representedName
                ? t('asProxy', namespace, { name: unit.representedName })
                : t('asDesignatedVoter', namespace)}
            </>
          )}
          {votedStatus(unit) !== null && (
            <>
              {' · '}
              <Text as="span" size="body-small" weight="600" color="Neutral800">
                {votedStatus(unit)}
              </Text>
            </>
          )}
        </Text>
      ))}
      {quorumUnits.length > 0 && (
        <TipMessage className="mt-2" title={t('quorumTipTitle', namespace)}>
          {onlyQuorum ? t('quorumTipOnly', namespace) : t('quorumTip', namespace)}
        </TipMessage>
      )}
    </div>
  );
};
